import * as React from "react";

import { Grid } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { ChartsColorPalette, LineSeriesType } from "@mui/x-charts";
import { LineChart } from "@mui/x-charts/LineChart";
import { MakeOptional } from "@mui/x-internals/types";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import { axiosCacheApi } from "../../../interceptors/axios";

// TypeScript için interface tanımlamaları
interface AreaGradientProps {
  color: string;
  id: string;
}

// API'den gelen veri yapısı (yeni tip)
type StatisticData = {
  creationTime: string;
  isGraduationVerified: boolean;
  emailConfirmed: boolean;
}[];

interface SeriesItem {
  id: string;
  label: string;
  showMark: boolean;
  curve: "linear" | "natural" | "step" | "bump" | "monotone";
  data: number[];
  area: boolean;
  color?: string;
}

interface ProcessedData {
  xAxisData: string[];
  totalCounts: number[];
  emailConfirmedCounts: number[];
  graduationVerifiedCounts: number[];
  datesByPeriod: Record<
    string,
    { total: number; emailConfirmed: number; graduationVerified: number }
  >;
  totalSessions: number;
  totalConfirmedEmails: number;
  totalVerifiedGraduations: number;
  percentageChange: number;
}

type ViewMode = "daily" | "hourly";

// Alan için gradient oluşturan bileşen
function AreaGradient({ color, id }: AreaGradientProps) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

// Tarih verilerini işleyen yardımcı fonksiyon
function processDateData(
  data: StatisticData,
  viewMode: ViewMode
): ProcessedData {
  // Tarihleri dayjs nesnelerine dönüştürme
  const dayjsDates = data.map((item) => ({
    date: dayjs(item.creationTime),
    emailConfirmed: item.emailConfirmed,
    isGraduationVerified: item.isGraduationVerified,
  }));

  // Tarihleri periyotlara göre gruplandırma (günlük veya saatlik)
  const datesByPeriod: Record<
    string,
    { total: number; emailConfirmed: number; graduationVerified: number }
  > = {};

  dayjsDates.forEach((item) => {
    let periodKey: string;

    if (viewMode === "daily") {
      periodKey = item.date.format("YYYY-MM-DD");
    } else {
      // Saatlik görünüm için
      periodKey = item.date.format("YYYY-MM-DD HH:00");
    }

    if (!datesByPeriod[periodKey]) {
      datesByPeriod[periodKey] = {
        total: 0,
        emailConfirmed: 0,
        graduationVerified: 0,
      };
    }

    datesByPeriod[periodKey].total += 1;

    if (item.emailConfirmed) {
      datesByPeriod[periodKey].emailConfirmed += 1;
    }

    if (item.isGraduationVerified) {
      datesByPeriod[periodKey].graduationVerified += 1;
    }
  });

  // Periyotları oluştur
  const today = dayjs();
  const allPeriods: string[] = [];
  const totalCounts: number[] = [];
  const emailConfirmedCounts: number[] = [];
  const graduationVerifiedCounts: number[] = [];

  if (viewMode === "daily") {
    // Son 30 günü kapsayacak şekilde tüm günleri oluştur
    const thirtyDaysAgo = today.subtract(29, "day");

    for (let i = 0; i < 30; i++) {
      const currentDay = thirtyDaysAgo.add(i, "day");
      const periodKey = currentDay.format("YYYY-MM-DD");
      const formattedPeriod = currentDay.format("DD MMM");

      allPeriods.push(formattedPeriod);

      if (datesByPeriod[periodKey]) {
        totalCounts.push(datesByPeriod[periodKey].total);
        emailConfirmedCounts.push(datesByPeriod[periodKey].emailConfirmed);
        graduationVerifiedCounts.push(
          datesByPeriod[periodKey].graduationVerified
        );
      } else {
        totalCounts.push(0);
        emailConfirmedCounts.push(0);
        graduationVerifiedCounts.push(0);
      }
    }
  } else {
    // Son 24 saati kapsayacak şekilde tüm saatleri oluştur
    const twentyFourHoursAgo = today.subtract(23, "hour");

    for (let i = 0; i < 24; i++) {
      const currentHour = twentyFourHoursAgo.add(i, "hour");
      const periodKey = currentHour.format("YYYY-MM-DD HH:00");
      const formattedPeriod = currentHour.format("HH:mm");

      allPeriods.push(formattedPeriod);

      if (datesByPeriod[periodKey]) {
        totalCounts.push(datesByPeriod[periodKey].total);
        emailConfirmedCounts.push(datesByPeriod[periodKey].emailConfirmed);
        graduationVerifiedCounts.push(
          datesByPeriod[periodKey].graduationVerified
        );
      } else {
        totalCounts.push(0);
        emailConfirmedCounts.push(0);
        graduationVerifiedCounts.push(0);
      }
    }
  }

  // Toplam sayılar
  const totalSessions = data.length;
  const totalConfirmedEmails = data.filter(
    (item) => item.emailConfirmed
  ).length;
  const totalVerifiedGraduations = data.filter(
    (item) => item.isGraduationVerified
  ).length;

  // Yüzde değişimi hesaplama
  let percentageChange = 0;
  if (viewMode === "daily") {
    // Son 7 gün ve önceki 7 günün verileri
    const last7DaysData = totalCounts
      .slice(-7)
      .reduce((acc, curr) => acc + curr, 0);
    const previous7DaysData = totalCounts
      .slice(-14, -7)
      .reduce((acc, curr) => acc + curr, 0);

    if (previous7DaysData > 0) {
      percentageChange = Math.round(
        ((last7DaysData - previous7DaysData) / previous7DaysData) * 100
      );
    }
  } else {
    // Son 12 saat ve önceki 12 saatin verileri
    const last12HoursData = totalCounts
      .slice(-12)
      .reduce((acc, curr) => acc + curr, 0);
    const previous12HoursData = totalCounts
      .slice(-24, -12)
      .reduce((acc, curr) => acc + curr, 0);

    if (previous12HoursData > 0) {
      percentageChange = Math.round(
        ((last12HoursData - previous12HoursData) / previous12HoursData) * 100
      );
    }
  }

  return {
    xAxisData: allPeriods,
    totalCounts,
    emailConfirmedCounts,
    graduationVerifiedCounts,
    datesByPeriod,
    totalSessions,
    totalConfirmedEmails,
    totalVerifiedGraduations,
    percentageChange,
  };
}

export default function SessionsChart(): JSX.Element {
  const theme = useTheme();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [statisticData, setStatisticData] =
    React.useState<StatisticData | null>(null);
  const [processedData, setProcessedData] =
    React.useState<ProcessedData | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [viewMode, setViewMode] = React.useState<ViewMode>("daily");

  // ViewMode değiştiğinde verileri yeniden işle
  React.useEffect(() => {
    if (statisticData) {
      const processed = processDateData(statisticData, viewMode);
      setProcessedData(processed);
    }
  }, [statisticData, viewMode]);

  // API'den veri çekme
  React.useEffect(() => {
    const fetchStatistics = async (): Promise<void> => {
      setLoading(true);
      try {
        const response = await axiosCacheApi.get<StatisticData>(
          "api/users/statistic"
        );
        setStatisticData(response.data);

        // Tarih verilerini işle
        const processed = processDateData(response.data, viewMode);
        setProcessedData(processed);

        setLoading(false);
      } catch (err) {
        console.error("İstatistik verileri alınırken hata:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Veriler yüklenirken bir hata oluştu"
        );
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewMode: ViewMode
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // Yükleme durumu
  if (loading) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography>Veriler yükleniyor...</Typography>
        </CardContent>
      </Card>
    );
  }

  // Hata durumu
  if (error) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography color="error">Hata: {error}</Typography>
        </CardContent>
      </Card>
    );
  }

  // Veriler yoksa
  if (!processedData) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography>Veri bulunamadı</Typography>
        </CardContent>
      </Card>
    );
  }

  // Veri serilerini oluştur
  const generateSeries: MakeOptional<LineSeriesType, "type">[] = [
    {
      id: "sessions",
      label: "Tüm kayıtlar",
      showMark: false,
      curve: "linear",
      data: processedData.totalCounts,
      area: true,
      color: theme.palette.primary.main,
    },
    {
      id: "emailConfirmed",
      label: "E-posta onaylı",
      showMark: false,
      curve: "linear",
      data: processedData.emailConfirmedCounts,
      area: true,
      color: theme.palette.success.main,
    },
    {
      id: "graduationVerified",
      label: "Mezuniyet doğrulaması yapılmış",
      showMark: false,
      curve: "linear",
      data: processedData.graduationVerifiedCounts,
      area: true,
      color: theme.palette.info.main,
    },
  ];

  const colorPalate: ChartsColorPalette = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.info.main,
  ];

  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardContent>
        <Grid container spacing={2}>
          {/* Toggle Button Group */}
          <Grid size={{ xs: 12 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                aria-label="görünüm modu"
                size="small"
              >
                <ToggleButton value="daily" aria-label="günlük">
                  Günlük
                </ToggleButton>
                <ToggleButton value="hourly" aria-label="saatlik">
                  Saatlik
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Stack>
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="h5" component="p">
                  {processedData.totalSessions.toLocaleString("tr-TR")}
                </Typography>
                <Chip
                  size="small"
                  color={
                    processedData.percentageChange >= 0 ? "success" : "error"
                  }
                  label={`${processedData.percentageChange >= 0 ? "+" : ""}${
                    processedData.percentageChange
                  }%`}
                />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: theme.palette.primary.main,
                  }}
                />
                <Typography variant="subtitle2">Tüm kayıtlar</Typography>
              </Stack>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Stack>
              <Typography variant="h5" component="p">
                {processedData.totalConfirmedEmails.toLocaleString("tr-TR")}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: theme.palette.success.main,
                  }}
                />
                <Typography variant="subtitle2">E-posta onaylı</Typography>
              </Stack>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Stack>
              <Typography variant="h5" component="p">
                {processedData.totalVerifiedGraduations.toLocaleString("tr-TR")}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: theme.palette.info.main,
                  }}
                />
                <Typography variant="subtitle2">
                  Mezuniyet doğrulaması
                </Typography>
              </Stack>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {viewMode === "daily"
                ? "Son 30 gün için günlük istatistikler"
                : "Son 24 saat için saatlik istatistikler"}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <LineChart
              colors={colorPalate}
              xAxis={[
                {
                  scaleType: "point",
                  data: processedData.xAxisData,
                  tickInterval:
                    viewMode === "daily"
                      ? (index: number) => index % 5 === 0
                      : (index: number) => index % 8 === 0,
                },
              ]}
              series={generateSeries}
              height={377}
              grid={{ horizontal: true }}
              sx={{
                "& .MuiAreaElement-series-sessions": {
                  fill: "url('#sessions')",
                },
                "& .MuiAreaElement-series-emailConfirmed": {
                  fill: "url('#emailConfirmed')",
                },
                "& .MuiAreaElement-series-graduationVerified": {
                  fill: "url('#graduationVerified')",
                },
              }}
              slotProps={{
                legend: {
                  position: {
                    vertical: "top",
                    horizontal: "end",
                  },
                },
              }}
            >
              <AreaGradient color={theme.palette.primary.main} id="sessions" />
              <AreaGradient
                color={theme.palette.success.main}
                id="emailConfirmed"
              />
              <AreaGradient
                color={theme.palette.info.main}
                id="graduationVerified"
              />
            </LineChart>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
